/**
 * Universal Transverse Mercator on WGS84.
 *
 * The projection is the Gauss–Krüger transverse Mercator evaluated with Krüger's series to
 * sixth order in the third flattening n (C. F. F. Karney, "Transverse Mercator with an
 * accuracy of a few nanometers", J. Geodesy 85, 475–485, 2011), which is exact to a few
 * nanometres within about 3900 km of the central meridian — far beyond a 6° zone.
 *
 * UTM conventions (NGA / DMA TM 8358.1):
 * - WGS84 ellipsoid: a = 6378137 m, 1/f = 298.257223563.
 * - Scale factor on the central meridian k₀ = 0.9996; false easting 500 000 m; false
 *   northing 10 000 000 m in the southern hemisphere, 0 in the northern.
 * - Zones are 6° wide, numbered 1–60 eastward from 180°W; the central meridian of zone z
 *   is 6z − 183 degrees.
 * - Latitude bands C–X (omitting I and O) are 8° tall from 80°S, band X reaching 84°N.
 *   UTM does not cover the poles, which use Universal Polar Stereographic.
 * - Exceptions: zone 32V is widened to 3°E–12°E for south-west Norway (zone 31V shrinks to
 *   0°–3°E), and in band X zones 31, 33, 35 and 37 cover 0–9°E, 9–21°E, 21–33°E and
 *   33–42°E while 32X, 34X and 36X do not exist.
 */

import {
  normalizeLongitude,
  WGS84_A,
  WGS84_F,
} from "@/lib/tool-kit/earth-science/geodesy";

export const UTM_SCALE = 0.9996;
export const UTM_FALSE_EASTING = 500_000;
export const UTM_FALSE_NORTHING_SOUTH = 10_000_000;
export const UTM_MIN_LATITUDE = -80;
export const UTM_MAX_LATITUDE = 84;

const N = WGS84_F / (2 - WGS84_F);
const E = Math.sqrt(WGS84_F * (2 - WGS84_F));
const N2 = N * N;
const N3 = N2 * N;
const N4 = N3 * N;
const N5 = N4 * N;
const N6 = N5 * N;
/** Rectifying radius. */
const RADIUS_A = (WGS84_A / (1 + N)) * (1 + N2 / 4 + N4 / 64 + N6 / 256);

/** Krüger series coefficients for the forward projection (Karney 2011, eq. 35). */
const ALPHA = [
  N / 2 -
    (2 * N2) / 3 +
    (5 * N3) / 16 +
    (41 * N4) / 180 -
    (127 * N5) / 288 +
    (7891 * N6) / 37_800,
  (13 * N2) / 48 -
    (3 * N3) / 5 +
    (557 * N4) / 1440 +
    (281 * N5) / 630 -
    (1_983_433 * N6) / 1_935_360,
  (61 * N3) / 240 -
    (103 * N4) / 140 +
    (15_061 * N5) / 26_880 +
    (167_603 * N6) / 181_440,
  (49_561 * N4) / 161_280 - (179 * N5) / 168 + (6_601_661 * N6) / 7_257_600,
  (34_729 * N5) / 80_640 - (3_418_889 * N6) / 1_995_840,
  (212_378_941 * N6) / 319_334_400,
];

/** Krüger series coefficients for the inverse projection (Karney 2011, eq. 36). */
const BETA = [
  N / 2 -
    (2 * N2) / 3 +
    (37 * N3) / 96 -
    N4 / 360 -
    (81 * N5) / 512 +
    (96_199 * N6) / 604_800,
  N2 / 48 +
    N3 / 15 -
    (437 * N4) / 1440 +
    (46 * N5) / 105 -
    (1_118_711 * N6) / 3_870_720,
  (17 * N3) / 480 - (37 * N4) / 840 - (209 * N5) / 4480 + (5569 * N6) / 90_720,
  (4397 * N4) / 161_280 - (11 * N5) / 504 - (830_251 * N6) / 7_257_600,
  (4583 * N5) / 161_280 - (108_847 * N6) / 3_991_680,
  (20_648_693 * N6) / 638_668_800,
];

const RAD = Math.PI / 180;
const DEG = 180 / Math.PI;
const BANDS = "CDEFGHJKLMNPQRSTUVWX";

/** A longitude within −180…180, keeping both ends as typed (−180 starts zone 1, 180 ends zone 60). */
function wrapForZone(lon: number): number {
  return lon >= -180 && lon <= 180 ? lon : normalizeLongitude(lon);
}

/** The UTM zone number (1–60) for a point, with the Norway and Svalbard exceptions. */
export function utmZone(lat: number, lon: number): number {
  const wrapped = wrapForZone(lon);
  let zone = Math.floor((wrapped + 180) / 6) + 1;
  if (lat >= 56 && lat < 64 && wrapped >= 3 && wrapped < 12) {
    zone = 32;
  }
  if (lat >= 72 && lat < 84.000_000_1) {
    if (wrapped >= 0 && wrapped < 9) {
      zone = 31;
    } else if (wrapped >= 9 && wrapped < 21) {
      zone = 33;
    } else if (wrapped >= 21 && wrapped < 33) {
      zone = 35;
    } else if (wrapped >= 33 && wrapped < 42) {
      zone = 37;
    }
  }
  return Math.min(60, zone);
}

/** The latitude band letter C–X, or null outside 80°S–84°N. */
export function utmBand(lat: number): string | null {
  if (lat < UTM_MIN_LATITUDE || lat > UTM_MAX_LATITUDE) {
    return null;
  }
  return BANDS[Math.min(BANDS.length - 1, Math.floor((lat + 80) / 8))];
}

/** The central meridian of a zone, in degrees east. */
export function centralMeridian(zone: number): number {
  return (zone - 1) * 6 - 180 + 3;
}

export interface Utm {
  zone: number;
  /** Latitude band letter. */
  band: string;
  hemisphere: "N" | "S";
  /** Metres. */
  easting: number;
  /** Metres. */
  northing: number;
  /** True when the zone is one of the Norway or Svalbard exceptions. */
  exception: boolean;
}

/** Conformal latitude of a geodetic latitude (radians). */
function conformalLatitude(phi: number): number {
  const sinPhi = Math.sin(phi);
  return Math.atan(
    Math.sinh(Math.asinh(Math.tan(phi)) - E * Math.atanh(E * sinPhi))
  );
}

/** Project a point onto the transverse Mercator plane of `zone`, or null outside UTM's latitudes. */
export function toUtm(
  lat: number,
  lon: number,
  forcedZone?: number
): Utm | null {
  const band = utmBand(lat);
  if (band === null || !Number.isFinite(lon)) {
    return null;
  }
  const zone = forcedZone ?? utmZone(lat, lon);
  const dLambda = normalizeLongitude(lon - centralMeridian(zone)) * RAD;
  const chi = conformalLatitude(lat * RAD);
  const tanChi = Math.tan(chi);
  const xiPrime = Math.atan2(tanChi, Math.cos(dLambda));
  const etaPrime = Math.asinh(
    Math.sin(dLambda) / Math.hypot(tanChi, Math.cos(dLambda))
  );
  let xi = xiPrime;
  let eta = etaPrime;
  for (const [index, alpha] of ALPHA.entries()) {
    const j = 2 * (index + 1);
    xi += alpha * Math.sin(j * xiPrime) * Math.cosh(j * etaPrime);
    eta += alpha * Math.cos(j * xiPrime) * Math.sinh(j * etaPrime);
  }
  const hemisphere = lat >= 0 ? "N" : "S";
  const gridZone = Math.min(60, Math.floor((wrapForZone(lon) + 180) / 6) + 1);
  return {
    zone,
    band,
    hemisphere,
    easting: UTM_FALSE_EASTING + UTM_SCALE * RADIUS_A * eta,
    northing:
      UTM_SCALE * RADIUS_A * xi +
      (hemisphere === "S" ? UTM_FALSE_NORTHING_SOUTH : 0),
    exception: forcedZone === undefined && zone !== gridZone,
  };
}

/** Geodetic latitude (radians) from conformal latitude, by Newton's method on the exact relation. */
function geodeticFromConformal(chi: number): number {
  let phi = chi;
  for (let i = 0; i < 12; i++) {
    const sinPhi = Math.sin(phi);
    const current = conformalLatitude(phi);
    // dχ/dφ = (1 − e²) cos φ / ((1 − e² sin²φ) cos χ)
    const slope =
      ((1 - E * E) * Math.cos(phi)) /
      ((1 - E * E * sinPhi * sinPhi) * Math.cos(current));
    const next = phi - (current - chi) / slope;
    if (Math.abs(next - phi) < 1e-15) {
      return next;
    }
    phi = next;
  }
  return phi;
}

export interface UtmInput {
  zone: number;
  hemisphere: "N" | "S";
  easting: number;
  northing: number;
}

/** Latitude and longitude (degrees) from UTM, on WGS84. */
export function fromUtm(input: UtmInput): { lat: number; lon: number } {
  const northing =
    input.hemisphere === "S"
      ? input.northing - UTM_FALSE_NORTHING_SOUTH
      : input.northing;
  const xi = northing / (UTM_SCALE * RADIUS_A);
  const eta = (input.easting - UTM_FALSE_EASTING) / (UTM_SCALE * RADIUS_A);
  let xiPrime = xi;
  let etaPrime = eta;
  for (const [index, beta] of BETA.entries()) {
    const j = 2 * (index + 1);
    xiPrime -= beta * Math.sin(j * xi) * Math.cosh(j * eta);
    etaPrime -= beta * Math.cos(j * xi) * Math.sinh(j * eta);
  }
  const chi = Math.asin(Math.sin(xiPrime) / Math.cosh(etaPrime));
  const dLambda = Math.atan2(Math.sinh(etaPrime), Math.cos(xiPrime));
  return {
    lat: geodeticFromConformal(chi) * DEG,
    lon: normalizeLongitude(centralMeridian(input.zone) + dLambda * DEG),
  };
}
